/** @format */

import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import OtpVerificationProcess from "../common/custom/Verification/OtpVerificationProcess";
import { sessions } from "../common/localstorage/SessionStorage";
import { GetMessageReponse } from "../common/Logic/ServerResponse";
import { FeedbackDto, Validation } from "../common/models/requests/FeedbackDto";
import {
  deleteProfileThunk,
  selectApplicant
} from "../features/applicant/ApplicantSlice";
import {
  AccessToken,
  CheckDeleteLegibility,
} from "../features/applicant/Models";
import {
  GetOtpThunk,
  VerifyOtpThunk,
  selectLogin,
} from "../features/login/loginSlice";
import { SendVerificationCode } from "../features/login/models";


export default function DeleteApplication() {
  const [open, setOpen] = React.useState(false);
  const selectApplication = useAppSelector(selectApplicant);
  const [data, setData] = useState<AccessToken>();
  const dispatch = useAppDispatch();
  const loginSelect = useAppSelector(selectLogin);
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [expiryDate, setExpiryDate] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (selectApplication.loginState.accessToken) {
      setData(selectApplication.loginState.accessToken);
    }
  }, [selectApplication.loginState.accessToken]);

  const validationSchema = yup.object({
    identityNumber: yup
      .string()
      .required("Identity Number is required")
      .matches(/^[0-9]+$/, "Identity number should contain only digits")
      .matches(
        /(([0-9]{2})(0|1)([0-9])([0-3])([0-9]))([ ]?)(([0-9]{4})([ ]?)([0-1][8]([ ]?)[0-9]))/gm,
        "A valid identity number must be 13 digits"
      )
      .min(13, "A valid identity number must be 13 digits")
      .max(13, "A valid identity number must be 13 digits"),
  });

  const validationSchemaSendVerification = yup.object({
    otp: yup.string().required("OTP is required").min(5).max(5),
  });

  const formik: FormikProps<CheckDeleteLegibility> =
    useFormik<CheckDeleteLegibility>({
      initialValues: {
        identityNumber: "",
      },
      enableReinitialize: true,
      validationSchema: validationSchema,
      onSubmit: (values: CheckDeleteLegibility) => {},
    });

  //TODO: send verification code, verify code, show legibility,

  useEffect(() => {
    if (loginSelect.loginState.expiryDate) {
      setExpiryDate(loginSelect.loginState.expiryDate);
    }
  }, [loginSelect.loginState.expiryDate]);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deletedUser, setDeletedUser] = useState('');

  const handleDeleteApplication = () => {
    setIsDeleting(true);
    
    dispatch(GetOtpThunk(formik.values.identityNumber)).then((data) => {
      if (loginSelect.loginState.status === "failed" || data.payload?.errors) {
        const feed = GetMessageReponse(
          loginSelect.loginState.error,
          false,
          data.type
        );
        setFeedback(feed);
        setIsDeleting(false);
        return;
      }
    });
  };

  const formikSendCode: FormikProps<SendVerificationCode> =
    useFormik<SendVerificationCode>({
      initialValues: {
        otp: "",
        identityNumber: "",
      },
      validationSchema: validationSchemaSendVerification,
      enableReinitialize: true,
      onSubmit: (values: SendVerificationCode) => {
        setIsVerifying(true);
        values.identityNumber = formik.values.identityNumber;
        dispatch(VerifyOtpThunk(values)).then((response) => {
          if (
            loginSelect.loginState.status === "failed" ||
            response.payload?.errors
          ) {
            const feed = GetMessageReponse(
              loginSelect.loginState.error,
              false,
              response.type
            );
            setFeedback(feed);
            setIsVerifying(false);
            return;
          }

          if (
            (response.type.includes("fulfilled") &&
              response.payload?.validation.code === 201) ||
            (response.type.includes("fulfilled") &&
              response.payload?.validation.code === 200)
          ) {
            if (response.payload?.data === false) {
              const feed = GetMessageReponse(
                {
                  code: 400,
                  messages: ["We could not validate the OTP"],
                } as Validation,
                false,
                response.type
              );
              setFeedback(feed);
              return;
            }

            // Now delete application
            setIsDeleting(true);
            dispatch(deleteProfileThunk(formik.values.identityNumber)).then(
              (deleteresponse) => {
                if (
                  selectApplication.deleteProfileState.status === "failed" ||
                  deleteresponse.payload?.errors
                ) {
                  const feed = GetMessageReponse(
                    selectApplication.deleteProfileState.error,
                    false,
                    deleteresponse.type
                  );
                  setFeedback(feed);
                  setIsDeleting(false);
                  return;
                }
                if (
                  (deleteresponse.type.includes("fulfilled") &&
                    deleteresponse.payload?.validation.code === 201) ||
                  (deleteresponse.type.includes("fulfilled") &&
                    deleteresponse.payload?.validation.code === 200)
                ) {
                  setDeletedUser(data?.displayName || 'User');
                  setShowSuccess(true);
                  sessions.removeToken();
                  
                  // Navigate after showing success message
                  setTimeout(() => {
                    navigate("/");
                    window.location.reload();
                  }, 4000);
                } else {
                  setIsDeleting(false);
                }
              }
            );
          }
        });
      },
    });

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
      <React.Fragment>
        <Button
          variant="contained"
          color="error"
          sx={{ ml: 3 }}
          startIcon={
            selectApplication.deleteProfileState.status === "loading" ? (
              <CircularProgress
                style={{ width: 15, height: 15, color: "#fff" }}
              />
            ) : (
              <DeleteForeverIcon />
            )
          }
          onClick={() => handleClickOpen()}
        >
          Delete Application
        </Button>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
          <DialogTitle>
            <Box
              sx={{
                position: "relative",
              }}
            >
              <Box>Delete Application</Box>
              <IconButton
                sx={{ position: "absolute", right: 0, top: 0 }}
                onClick={() => handleClose()}
              >
                <CloseIcon sx={{ cursor: "pointer" }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Alert severity="warning">
              Deleting your information is permanent and cannot be undone.This
              action will delete your personal information and application
              details including any documents already uploaded
            </Alert>

            {data && data.application.canDeleteApplication ? (
              <Box mt={3}>
                <Alert severity="success">
                  {`Hi ${
                    data && data?.displayName
                  }, you have opted to delete your application. To complete the deletion process, please enter your ID number and verify the OTP from your the email address you registered with`}
                </Alert>
              </Box>
            ) : (
              <Box mt={3}>
                <Box mt={3}>
                  <Alert severity="info">
                    {`Dear ${
                      data && data?.displayName
                    }, You are not currently eligible to delete your application. To proceed with the deletion of your
                      application, please complete the attached `}
                    <a
                      target="_blank"
                      href="https://saicawebprstorage.blob.core.windows.net/uploads/TEUF_ErasureRequestForm_V1_260521.pdf"
                    >
                      form
                    </a>{" "}
                    and send it to Thuthuka@saica.co.za.
                  </Alert>
                </Box>
              </Box>
            )}

            {data && data.application.canDeleteApplication && (
              <Box mt={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="identityNumber"
                      name="identityNumber"
                      label="Enter SA Identity Number"
                      value={formik.values.identityNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      type="text"
                      error={
                        formik.touched.identityNumber &&
                        Boolean(formik.errors.identityNumber)
                      }
                      helperText={
                        formik.touched.identityNumber &&
                        (formik.errors.identityNumber as React.ReactNode)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={
                                loginSelect.loginState.status === "loading" ? (
                                  <CircularProgress
                                    style={{
                                      width: 15,
                                      height: 15,
                                      color: "#fff",
                                    }}
                                  />
                                ) : (
                                  <DeleteForeverIcon />
                                )
                              }
                              onClick={() => handleDeleteApplication()}
                            >
                              <Hidden mdDown>Yes, Delete Application</Hidden>
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <OtpVerificationProcess
                      expiryDate={expiryDate}
                      identityNumber={formik.values.identityNumber}
                      setExpiryDate={setExpiryDate}
                      formikSendCode={formikSendCode}
                    />
                    {expiryDate && (
                      <LoadingButton
                        sx={{ mt: 3 }}
                        color="info"
                        variant="contained"
                        size="medium"
                        startIcon={<VerifiedUserIcon />}
                        onClick={() => {
                          formikSendCode.submitForm();
                        }}
                        loading={
                          isVerifying ||
                          selectApplication.applicantCreateState.status === "loading" ||
                          loginSelect.loginState.status === "loading"
                        }
                        loadingPosition="start"
                        loadingIndicator={
                          <CircularProgress size={20} color="inherit" />
                        }
                        disabled={!formikSendCode.isValid}
                      >
                        Verify OTP
                      </LoadingButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </React.Fragment>
      
      {/* Success Dialog */}
      <Dialog open={showSuccess} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h6" align="center">
              {deletedUser}'s Profile Successfully deleted
            </Typography>
            <Typography>You will be redirected to the home page shortly...</Typography>
            <CircularProgress size={24} sx={{ mt: 2 }} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
